/**
 * Player-facing shift flow types for the local three-story vertical slice.
 * No external state library — pure typed state + reducer.
 */
import type { ChallengeConstraintId, ScoreBreakdown } from "@desk/core";
import type { PackId } from "../content/registry.js";
import type { GamePresentation } from "./challenge/presentation.js";

export type ScreenId =
  | "home"
  | "brief"
  | "editor"
  | "compare"
  | "recap"
  | "portfolio"
  | "roadmap"
  | "challenge-landing"
  | "challenge-invalid";

/** Tool / expression unlocks introduced by story completion. */
export type UnlockId =
  | "cut"
  | "frame"
  | "text"
  | "sound"
  | "cast"
  | "captionDeep"
  | "soundDeep";

/** Expressive store sidegrades — never Reach multipliers. */
export type StoreItemId =
  | "precision-trim"
  | "caption-style-reveal"
  | "sound-sting-pack"
  | "export-card-theme"
  | "extra-draft-slot"
  | "headline-pack"
  | "sfx-pack"
  | "looks-pack"
  | "motion-pack"
  | "sticker-pack-reactions"
  | "sticker-pack-effects"
  | "seamless-transitions";

/** Equipped result-card chrome — separate from purchase ownership. */
export type EquippedResultCardTheme = "default" | "portfolio-alt";

export interface Economy {
  /** Cumulative public score — never spent. */
  reach: number;
  /** Gates chapter / story unlocks (soft thresholds always reachable via publish). */
  followers: number;
  /** Spent on expressive store items. */
  cash: number;
  /** Delayed reputation — non-spendable. */
  cred: number;
}

export interface PublishRewards {
  reachGain: number;
  followersGain: number;
  cashGain: number;
  credDelta: number;
}

/**
 * What a single publish actually PAID OUT under the best-score delta model
 * (Job B). `followers` / `cash` are the amounts credited to the economy THIS
 * publish — the improvement over the crowd's banked best, plus any one-time
 * 3/3 clean-sweep bonus. `improved` is true when this cut beat the crowd's
 * prior banked reward; `firstLanding` when it is the first time this crowd was
 * landed. Presentation metadata attached to the publish snapshot so the reward
 * screens can celebrate improvement ("new best!") vs a replay that didn't beat
 * the bank ("your best still stands"). Never feeds `@desk/core` scoring.
 */
export interface EarnedReward {
  /** Followers credited this publish (crowd delta + any clean-sweep bonus). */
  followers: number;
  /** Bits/cash credited this publish (crowd delta + any clean-sweep bonus). */
  cash: number;
  /** True when this cut beat the crowd's banked best reward (a new best). */
  improved: boolean;
  /** True when this publish landed the crowd. */
  landed: boolean;
  /** True when this was the first landing recorded for the crowd. */
  firstLanding: boolean;
  /** True when this publish completed the level 3/3 for the first time. */
  cleanSweep: boolean;
}

export interface SimulatedComment {
  id: string;
  handle: string;
  body: string;
  tone: "hype" | "curious" | "skeptical" | "neutral";
}

export interface CompareBeatRow {
  id: string;
  label: string;
}

export interface CompareClaimRow {
  claimId: string;
  text: string;
  status: "supported" | "unsupported" | "contradicted";
}

export interface CompareFrameRow {
  beatId: string;
  beatLabel: string;
  entityId: string;
  entityLabel: string;
}

export interface CompareSoundRow {
  assetId: string;
  role: string;
  note: string;
}

export interface CompareCastRow {
  slotId: string;
  variantId: string;
  variantLabel: string;
  sourceFaithful: boolean;
  disclosureShown: boolean;
}

export interface CompareLookRow {
  treatmentId: string;
  intensityMilli?: number;
}

export interface CompareMotionRow {
  beatId: string;
  presetId: string;
}

export interface CompareSfxRow {
  id: string;
  assetId: string;
  atTick: number;
  volumeMilli: number;
}

export interface CompareStickerRow {
  /** The sticker INSTANCE id (not a fixed slot — stickers are unlimited). */
  id: string;
  stickerId: string;
  startTick: number;
  endTick: number;
}

export interface CompareTransitionRow {
  beatId: string;
  presetId: string;
  durationTicks: number;
}

/** Same-source artifact comparison — plain language, not moral labels. */
export interface SourceCompareReport {
  retainedBeats: CompareBeatRow[];
  omittedBeats: CompareBeatRow[];
  trimmedBeats: CompareBeatRow[];
  reordered: boolean;
  reorderOrder: string[];
  frameExcludedCarriers: CompareFrameRow[];
  captionClaims: CompareClaimRow[];
  soundPriming: CompareSoundRow[];
  cast: CompareCastRow[];
  look: CompareLookRow | null;
  motions: CompareMotionRow[];
  sfx: CompareSfxRow[];
  stickers: CompareStickerRow[];
  transitions: CompareTransitionRow[];
  showsReconstructionLabel: boolean;
}

export interface PublishSnapshot {
  packId: PackId;
  packVersion: string;
  seed: number;
  graphJson: string;
  score: ScoreBreakdown;
  rewards: PublishRewards;
  /**
   * What this publish actually paid out under the best-score delta model
   * (Job B). Attached by the PUBLISH reducer after banking; absent on challenge
   * snapshots (challenges never pay campaign economy) and on old saves. Additive
   * over the base v1 save; never validated as required, never feeds scoring.
   */
  earned?: EarnedReward;
  comments: SimulatedComment[];
  compare: SourceCompareReport;
  /**
   * Game-side presentation levers (effects / caption placement / precision-trim
   * / reaction) captured at publish so the share link can reproduce the reel.
   * Never feeds `@desk/core` scoring; omitted on old snapshots.
   */
  presentation?: GamePresentation;
}

export interface DraftRecord {
  id: string;
  packId: PackId;
  packVersion: string;
  seed: number;
  graphJson: string;
  /** Monotonic sequence for deterministic eviction (not wall-clock). */
  seq: number;
}

export interface PortfolioEntry {
  id: string;
  packId: PackId;
  packVersion: string;
  seed: number;
  graphJson: string;
  score: Pick<
    ScoreBreakdown,
    "reach" | "credDelta" | "contextLoss" | "claimDrift" | "provenanceRisk"
  >;
  rewards: PublishRewards;
  seq: number;
}

export interface CurrentEdit {
  packId: PackId;
  packVersion: string;
  seed: number;
  graphJson: string;
  /**
   * The crowd this in-progress edit is cutting for (the audience objective).
   * Absent = fall back to the pack's served crowd. Presentation-layer only;
   * never feeds `@desk/core` scoring.
   */
  chosenCrowdId?: string;
  /**
   * Which flow spawned this edit. `"campaign"` (default when absent) is the
   * forward frontier flow that advances on a landed publish; `"roadmap"` is a
   * replay from the Desk hub that returns to the roadmap and never advances.
   */
  origin?: "campaign" | "roadmap";
}

/**
 * Per-audience result for one published edit against one crowd, computed in the
 * PRESENTATION layer from `editEmotionVector` / `crowdReaction` + the publish
 * `ScoreBreakdown`. Never produced by `@desk/core`.
 */
export interface AudienceResult {
  crowdId: string;
  /** Hit all the crowd's wants without tipping into their backlash. */
  landed: boolean;
  /** Landed with low Doubt (no receipts) — the "skilled manipulator" win. */
  clean: boolean;
  /**
   * Match quality against the crowd's GREEN "wants" zone, `crowdFit(...) * 1000`
   * rounded (0..1000). Drives the followers/Bits reward scale (Job B). Additive
   * over the base v1 save; absent on old saves (defaulted in sanitize).
   */
  fitMilli?: number;
  /** Headline number, from `ScoreBreakdown.reach` (same edit). */
  reachMilli: number;
  /** Ground-truth core penalty, from `ScoreBreakdown.credDelta`. */
  credDeltaMilli: number;
  /** The receipts read: `editEmotionVector(...).Doubt * 1000`, rounded. */
  doubtMilli: number;
  /** Reaction face for display (from `crowdReaction`). */
  face: string;
  /** Short plain-words status for display (from `crowdReaction`). */
  status: string;
  /**
   * The serialized EditGraph of the most recent published edit for this
   * (story, crowd). Revisiting the audience from the roadmap loads this back
   * into `currentEdit` so the prior cut is editable. Additive over the base v1
   * save; absent on old saves (defaults are handled in `sanitizeGameStateDeep`).
   * Presentation-layer metadata; never feeds `@desk/core` scoring.
   */
  graphJson?: string;
  /**
   * BANKED best followers reward earned for this (story, crowd) under the
   * best-score delta model (Job B). A replay pays only `max(0, newReward -
   * rewardFollowers)` and then raises this bank. Additive over the base v1 save;
   * absent/0 on old saves, so a first replay on an old save pays the full best
   * (acceptable — noted in `sanitizeGameStateDeep`). Never feeds scoring.
   */
  rewardFollowers?: number;
  /** BANKED best Bits/cash reward earned for this (story, crowd). See above. */
  rewardCash?: number;
}

export interface GameSettings {
  /** Shortens UI animation durations only — never game logic / scoring. */
  testSpeed: boolean;
  /**
   * Persisted onboarding gate. Missing/invalid on load sanitizes to 0.
   * Bump HOME_INTRO_VERSION to re-show intro for pre-version saves.
   */
  homeIntroVersion: number;
}

/** Current home intro content version — show intro when save is below this. */
export const HOME_INTRO_VERSION = 1;

/** Inclusive upper bound for sanitized homeIntroVersion integers. */
export const MAX_HOME_INTRO_VERSION = 9999;

/** Ephemeral challenge attempt — must not advance campaign unless saved to portfolio. */
export interface ChallengeSession {
  status: "landing" | "editing" | "publish" | "compare" | "invalid";
  invalidReason?: string;
  packId: PackId;
  packVersion: string;
  seed: number;
  constraintIds: ChallengeConstraintId[];
  challengerGraphJson: string;
  /**
   * Game-side presentation levers decoded from the shared link's `?p=` sidecar,
   * restored into the editor on accept so the reel + "How it's landing"
   * reproduce the sharer's. Absent for old links / neutral edits.
   */
  challengerPresentation?: GamePresentation;
  challengerScore: Pick<
    ScoreBreakdown,
    "reach" | "credDelta" | "contextLoss" | "claimDrift" | "provenanceRisk"
  >;
  /** Soft timer start (performance.now / Date) for optional deadline bonus — never hard fail. */
  startedAtMs: number | null;
  playerEdit: CurrentEdit | null;
  playerPublish: PublishSnapshot | null;
  publishRevealed: boolean;
  deadlineBonusAwarded: boolean;
  /** Campaign UI fields restored on persist / dismiss — never overwritten by challenge edit. */
  campaignFreeze: {
    screen: ScreenId;
    currentEdit: CurrentEdit | null;
    lastPublish: PublishSnapshot | null;
    publishRevealed: boolean;
  };
}

export interface GameState {
  screen: ScreenId;
  storyIndex: number;
  completedStoryIds: PackId[];
  economy: Economy;
  unlocks: UnlockId[];
  purchases: StoreItemId[];
  /**
   * Active result-card theme. Owning `export-card-theme` unlocks portfolio-alt;
   * equip is separate from buy. Missing on old saves migrates via sanitize.
   */
  equippedTheme: EquippedResultCardTheme;
  drafts: DraftRecord[];
  portfolio: PortfolioEntry[];
  draftSlotLimit: number;
  portfolioSlotLimit: number;
  settings: GameSettings;
  currentEdit: CurrentEdit | null;
  lastPublish: PublishSnapshot | null;
  /** publish screen: distribution metrics first; compare reveals Cred. */
  publishRevealed: boolean;
  shiftComplete: boolean;
  seq: number;
  /**
   * Best per-audience result per (story, crowd) — the progression meta-layer.
   * Additive over the base v1 save; defaulted to `{}` in `sanitizeGameStateDeep`
   * so old saves load cleanly. Presentation-derived; never affects scoring.
   */
  audienceProgress: Partial<Record<PackId, Record<string, AudienceResult>>>;
  /** True when entered via QA `?scenario=` bypass. */
  qaBypass: boolean;
  /**
   * When set, player is in a challenge attempt.
   * Campaign economy / completedStoryIds / drafts must stay frozen unless
   * SAVE_CHALLENGE_TO_PORTFOLIO explicitly merges a portfolio row.
   */
  challenge: ChallengeSession | null;
}

export const SAVE_SCHEMA_VERSION = 1 as const;
export const DEFAULT_DRAFT_SLOTS = 2;
export const DEFAULT_PORTFOLIO_SLOTS = 4;
export const MAX_DRAFT_SLOTS = 6;
export const MAX_PORTFOLIO_SLOTS = 8;

/** Soft follower thresholds — first-story publish always clears story-2 gate. */
export const FOLLOWERS_FOR_STORY: readonly number[] = [0, 4, 12] as const;

export const STARTING_UNLOCKS: readonly UnlockId[] = [
  "cut",
  "frame",
  "text",
  "sound",
] as const;
